import React from 'react';
import Parse from 'parse';
import { v4 as uuid } from 'uuid';
import {
  Alert,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { localDeliveryTime } from './objectPushDelivery';

// Publishing settings for this LifeObject; sending is part of publish, not a
// separate composer that chooses content again.
export default function WebPushTrigger({
  lifeObject,
  value,
  onChange,
  disabled,
}) {
  const [summary, setSummary] = React.useState(null);
  const [history, setHistory] = React.useState([]);
  const [error, setError] = React.useState('');
  const [cancelling, setCancelling] = React.useState(false);
  const objectId = lifeObject?.id;
  const loadHistory = React.useCallback(async () => {
    if (!objectId) return [];
    return Parse.Cloud.run('listFollowerPushes', { lifeObjectId: objectId });
  }, [objectId]);
  React.useEffect(() => {
    let active = true;
    setSummary(null);
    setHistory([]);
    setError('');
    Promise.all([Parse.Cloud.run('getFollowerPushSummary'), loadHistory()])
      .then(([counts, items]) => {
        if (active) {
          setSummary(counts);
          setHistory(items);
        }
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, [loadHistory]);
  const pending = history.some((item) => item.status === 'scheduled');
  const uncertain = history.some((item) =>
    ['sending', 'delivery_uncertain'].includes(item.status)
  );
  const blocked = disabled || cancelling || pending || uncertain || !summary;
  const completed = history.some((item) =>
    ['accepted', 'partial'].includes(item.status)
  );
  const change = (patch) => onChange({ ...value, ...patch, requestId: uuid() });
  const cancel = async (id) => {
    setCancelling(true);
    setError('');
    try {
      const result = await Parse.Cloud.run('cancelFollowerPush', { id });
      if (!result.cancelled)
        throw Error(
          'This notification has already started. Refresh its status before trying again.'
        );
      setHistory(await loadHistory());
      change({ mode: 'off' });
    } catch (e) {
      setError(e.message);
    } finally {
      setCancelling(false);
    }
  };
  return (
    <Stack spacing={2} sx={{ mt: 3 }}>
      <Typography variant="h6">Notify your followers & subscribers</Typography>
      <Typography color="text.secondary">
        App followers receive app notifications. Browser subscribers receive web
        notifications. Each notification opens this LifeObject.
      </Typography>
      {summary && (
        <Typography>
          {summary.appCount || 0} app devices · {summary.browserCount} browsers
        </Typography>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {pending && (
        <Alert severity="info">
          A notification is already scheduled for this LifeObject. Cancel it
          below before changing its delivery.
        </Alert>
      )}
      {uncertain && (
        <Alert severity="info">
          A previous send is still being confirmed. Check its status before
          starting another notification.
        </Alert>
      )}
      {completed && (
        <Typography color="text.secondary">
          Editing this LifeObject does not resend a previous notification.
          Change the delivery settings only when you want to send another.
        </Typography>
      )}
      <TextField
        select
        label="Notification"
        value={value.mode}
        onChange={(e) => change({ mode: e.target.value })}
        disabled={blocked}
      >
        <MenuItem value="off">Do not send</MenuItem>
        <MenuItem value="now">Send now when I publish</MenuItem>
        <MenuItem value="later">Schedule</MenuItem>
      </TextField>
      {value.mode !== 'off' && (
        <>
          {summary?.browserCount === 0 && !summary?.appCount && (
            <Alert severity="info">
              No followers or subscribers have notifications enabled yet. You
              can save these settings as a draft.
            </Alert>
          )}
          <TextField
            label="Notification title"
            value={value.title}
            onChange={(e) => change({ title: e.target.value })}
            inputProps={{ maxLength: 100 }}
            disabled={blocked}
          />
          <TextField
            label="Notification message"
            multiline
            minRows={2}
            value={value.body}
            onChange={(e) => change({ body: e.target.value })}
            inputProps={{ maxLength: 300 }}
            disabled={blocked}
          />
          <TextField
            label="Image link (optional)"
            value={value.image}
            onChange={(e) => change({ image: e.target.value })}
            disabled={blocked}
          />
          {value.mode === 'later' && (
            <TextField
              label="Send at"
              type="datetime-local"
              value={localDeliveryTime(value.time)}
              onChange={(e) =>
                change({
                  time: e.target.value
                    ? new Date(e.target.value).toISOString()
                    : '',
                })
              }
              InputLabelProps={{ shrink: true }}
              helperText={`Your time zone: ${
                Intl.DateTimeFormat().resolvedOptions().timeZone
              }. Delivery usually starts within a minute.`}
              disabled={blocked}
            />
          )}
          <Typography color="text.secondary">
            Saving a draft does not send a notification. Existing audience and
            discovery requirements still apply when someone opens it.
          </Typography>
        </>
      )}
      {history.map((item) => (
        <Stack key={item.id} direction="row" spacing={2} alignItems="center">
          <Typography sx={{ flex: 1 }}>
            {item.title} · {item.status}
            {item.scheduledAt
              ? ` · ${new Date(item.scheduledAt).toLocaleString()}`
              : ''}
          </Typography>
          {item.status === 'scheduled' && (
            <Button
              disabled={disabled || cancelling}
              onClick={() => cancel(item.id)}
            >
              Cancel scheduled send
            </Button>
          )}
        </Stack>
      ))}
    </Stack>
  );
}
